import type { QueryClient } from '@tanstack/react-query';
import { api, ApiError, errorMessage } from '@/lib/api';
import { formatBytes } from '@/lib/format';
import { createStore } from '@/lib/store';
import { toast } from '@/lib/toast';
import { createMd5, toBase64 } from '@shared/md5';
import { MAX_FILE_BYTES, type FileItem, type RecordKind, type UploadStart } from '@shared/record';
import { keys } from './queries';

// Uploads files straight from the browser to the team's S3 bucket as multipart uploads: the server starts the upload
// and signs each part, the browser PUTs the parts in parallel, and the server joins them. Each part's MD5 is worked
// out before its link is asked for and signed into it, so S3 takes only the exact bytes the browser read. Uploads live
// outside React, so they carry on while the sheet is closed, and a failed one picks up from the parts S3 already holds.

const PARALLEL = 4;
const ATTEMPTS = 3;
const URL_BATCH = 20;
/** Bytes hashed between pauses, so the page stays responsive while a large part is hashed. */
const HASH_SLICE = 1024 * 1024;

export type UploadStatus = 'starting' | 'checking' | 'uploading' | 'finishing' | 'failed';

export interface UploadView {
  key: string;
  fileId: string | null;
  entityType: RecordKind;
  entityId: string;
  name: string;
  size: number;
  mime: string | null;
  status: UploadStatus;
  loaded: number;
  error: string | null;
  /** True when the bucket's CORS settings are the likely cause, so the message points the admin there. */
  cors: boolean;
}

interface Task extends UploadView {
  file: File;
  partSize: number;
  partCount: number;
  etags: Map<number, string>;
  partLoaded: Map<number, number>;
  /** Base64 MD5 of each part hashed so far. */
  md5s: Map<number, string>;
  urls: Map<number, string>;
  /** The file was chosen again to resume an interrupted upload, so the parts S3 holds are compared with it first. */
  verify: boolean;
  xhrs: Set<XMLHttpRequest>;
  stopped: boolean;
  qc: QueryClient;
}

const tasks = new Map<string, Task>();
export const uploadStore = createStore<{ items: UploadView[] }>({ items: [] });

function publish(): void {
  uploadStore.set({
    items: [...tasks.values()].map((t) => ({
      key: t.key,
      fileId: t.fileId,
      entityType: t.entityType,
      entityId: t.entityId,
      name: t.name,
      size: t.size,
      mime: t.mime,
      status: t.status,
      loaded: t.loaded,
      error: t.error,
      cors: t.cors,
    })),
  });
  syncUnloadGuard();
}

let guarded = false;
function onBeforeUnload(e: BeforeUnloadEvent): void {
  e.preventDefault();
}
function syncUnloadGuard(): void {
  const busy = [...tasks.values()].some((t) => t.status !== 'failed');
  if (busy && !guarded) window.addEventListener('beforeunload', onBeforeUnload);
  if (!busy && guarded) window.removeEventListener('beforeunload', onBeforeUnload);
  guarded = busy;
}

let progressFrame = 0;
function publishProgress(): void {
  if (progressFrame) return;
  progressFrame = requestAnimationFrame(() => {
    progressFrame = 0;
    publish();
  });
}

class PartError extends Error {
  constructor(
    message: string,
    readonly kind: 'network' | 'etag' | 'expired' | 'digest' | 'read' | 'http',
  ) {
    super(message);
  }
}

/** The base64 MD5 of one part, read in slices with a pause between them. */
async function partMd5(task: Task, partNumber: number): Promise<string> {
  const known = task.md5s.get(partNumber);
  if (known) return known;
  const start = (partNumber - 1) * task.partSize;
  const end = start + partBytes(task, partNumber);
  const md5 = createMd5();
  for (let at = start; at < end; at += HASH_SLICE) {
    if (task.stopped) throw new PartError('Stopped.', 'network');
    let bytes: ArrayBuffer;
    try {
      bytes = await task.file.slice(at, Math.min(end, at + HASH_SLICE)).arrayBuffer();
    } catch {
      throw new PartError('The browser can no longer read the file.', 'read');
    }
    md5.update(new Uint8Array(bytes));
  }
  const digest = toBase64(md5.digest());
  task.md5s.set(partNumber, digest);
  return digest;
}

/** "a1b2…" from the base64 form, to compare with the ETag S3 gives a part. */
function hexOf(base64: string): string {
  return Array.from(atob(base64), (ch) => ch.charCodeAt(0).toString(16).padStart(2, '0')).join('');
}

/**
 * Whether a part S3 holds has the same bytes as that part of the chosen file. S3 gives each part its MD5 in hex as
 * the ETag; under SSE-KMS encryption the ETag is something else, so those parts never match and are sent again.
 */
async function sameBytes(task: Task, partNumber: number, etag: string): Promise<boolean> {
  const hex = /^"?([0-9a-f]{32})"?$/i.exec(etag)?.[1];
  return !!hex && hexOf(await partMd5(task, partNumber)) === hex.toLowerCase();
}

function putPart(task: Task, partNumber: number, url: string, md5: string): Promise<string> {
  const start = (partNumber - 1) * task.partSize;
  const blob = task.file.slice(start, Math.min(task.size, start + task.partSize));
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    task.xhrs.add(xhr);
    xhr.open('PUT', url);
    // Signed into the URL, so S3 refuses the part unless the bytes it receives have this MD5.
    xhr.setRequestHeader('Content-MD5', md5);
    xhr.upload.onprogress = (e) => {
      task.partLoaded.set(partNumber, e.loaded);
      refreshLoaded(task);
    };
    xhr.onload = () => {
      task.xhrs.delete(xhr);
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader('ETag');
        if (!etag) {
          reject(new PartError('S3 took the part but the browser could not read its ETag header.', 'etag'));
          return;
        }
        task.partLoaded.set(partNumber, blob.size);
        resolve(etag);
        return;
      }
      if (xhr.status === 403 && /expired/i.test(xhr.responseText)) reject(new PartError('The upload link expired.', 'expired'));
      else if (xhr.status === 400 && /BadDigest/.test(xhr.responseText)) reject(new PartError('S3 received a part that differs from the file.', 'digest'));
      else reject(new PartError(`S3 answered ${xhr.status}.`, 'http'));
    };
    xhr.onerror = () => {
      task.xhrs.delete(xhr);
      reject(new PartError('The browser could not reach S3.', 'network'));
    };
    xhr.onabort = () => {
      task.xhrs.delete(xhr);
      reject(new PartError('Stopped.', 'network'));
    };
    xhr.send(blob);
  });
}

function refreshLoaded(task: Task): void {
  let loaded = 0;
  for (const [n, bytes] of task.partLoaded) if (!task.etags.has(n)) loaded += bytes;
  for (const n of task.etags.keys()) loaded += partBytes(task, n);
  task.loaded = Math.min(task.size, loaded);
  publishProgress();
}

function partBytes(task: Task, n: number): number {
  return n === task.partCount ? task.size - (task.partCount - 1) * task.partSize : task.partSize;
}

/** The signed URL for a part. Other waiting parts whose MD5 is already known (after a retry) share the request. */
async function urlFor(task: Task, partNumber: number, md5: string, pending: number[]): Promise<string> {
  const cached = task.urls.get(partNumber);
  if (cached) return cached;
  const others = pending.filter((n) => n !== partNumber && !task.urls.has(n) && task.md5s.has(n)).slice(0, URL_BATCH - 1);
  const parts = [{ partNumber, md5 }, ...others.map((n) => ({ partNumber: n, md5: task.md5s.get(n)! }))];
  const res = await api.post<{ urls: { partNumber: number; url: string }[] }>(`/files/${task.fileId}/parts`, { parts });
  for (const u of res.urls) task.urls.set(u.partNumber, u.url);
  return task.urls.get(partNumber)!;
}

/** Sets the part size the server planned. Hashes of parts cut at another size no longer apply. */
function planFor(task: Task, partSize: number, partCount: number): void {
  if (partSize !== task.partSize) {
    task.md5s.clear();
    task.urls.clear();
  }
  task.partSize = partSize;
  task.partCount = partCount;
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function sendParts(task: Task): Promise<void> {
  const queue: number[] = [];
  for (let n = 1; n <= task.partCount; n += 1) if (!task.etags.has(n)) queue.push(n);
  let failure: unknown = null;
  const worker = async () => {
    while (queue.length && !task.stopped && !failure) {
      const n = queue.shift()!;
      for (let attempt = 1; ; attempt += 1) {
        try {
          const md5 = await partMd5(task, n);
          const url = await urlFor(task, n, md5, queue);
          const etag = await putPart(task, n, url, md5);
          task.etags.set(n, etag);
          refreshLoaded(task);
          break;
        } catch (err) {
          if (task.stopped) return;
          task.partLoaded.delete(n);
          refreshLoaded(task);
          if (err instanceof PartError && err.kind === 'expired') task.urls.delete(n);
          const retryable = err instanceof PartError && err.kind !== 'etag' && err.kind !== 'read';
          if (!retryable || attempt >= ATTEMPTS) {
            failure = err;
            return;
          }
          task.urls.delete(n);
          await wait(800 * 2 ** (attempt - 1));
        }
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLEL, task.partCount) }, worker));
  if (failure) throw failure;
}

async function explainFailure(task: Task, err: unknown): Promise<void> {
  task.cors = false;
  if (err instanceof PartError && err.kind === 'read') {
    task.error = `The browser can no longer read ${task.name}, as it changed or moved on this device. Cancel, then upload it again.`;
    return;
  }
  if (err instanceof PartError && err.kind === 'etag') {
    task.cors = true;
    task.error = await corsMessage(
      `The bucket's CORS settings hide the ETag header, so the parts cannot be joined. Add ETag to ExposeHeaders for ${location.origin}.`,
    );
    return;
  }
  if (err instanceof PartError && err.kind === 'network') {
    if (!navigator.onLine) {
      task.error = 'You are offline. Retry once you are connected; the parts already sent are kept.';
      return;
    }
    const problem = await corsCheck();
    if (problem) {
      task.cors = true;
      task.error = problem;
      return;
    }
    task.error = 'The browser could not reach S3. Retry to carry on from the parts already sent.';
    return;
  }
  task.error = err instanceof PartError ? `${err.message} Retry to carry on from the parts already sent.` : errorMessage(err);
}

async function corsCheck(): Promise<string | null> {
  try {
    const res = await api.post<{ checked: boolean; problem: string | null }>('/files/cors-check', { origin: location.origin });
    return res.problem;
  } catch {
    return null;
  }
}

async function corsMessage(fallback: string): Promise<string> {
  return (await corsCheck()) ?? fallback;
}

async function run(task: Task): Promise<void> {
  try {
    if (!task.fileId) {
      task.status = 'starting';
      publish();
      const started = await api.post<UploadStart>('/files', {
        entityType: task.entityType,
        entityId: task.entityId,
        name: task.name,
        size: task.size,
        mime: task.mime,
      });
      if (task.stopped) {
        void api.post(`/files/${started.file.id}/abort`).catch(() => undefined);
        return;
      }
      task.fileId = started.file.id;
      planFor(task, started.partSize, started.partCount);
      void task.qc.invalidateQueries({ queryKey: keys.files(task.entityType, task.entityId) });
    }
    task.status = 'uploading';
    task.error = null;
    publish();
    await sendParts(task);
    if (task.stopped) return;
    task.status = 'finishing';
    publish();
    const parts = [...task.etags].sort((a, b) => a[0] - b[0]).map(([partNumber, etag]) => ({ partNumber, etag }));
    const done = await api.post<FileItem>(`/files/${task.fileId}/complete`, { parts });
    tasks.delete(task.key);
    publish();
    void task.qc.invalidateQueries({ queryKey: keys.record(task.entityType, task.entityId) });
    toast(`${done.name} uploaded`, { icon: 'check-circle' });
  } catch (err) {
    if (task.stopped) return;
    for (const x of task.xhrs) x.abort();
    task.status = 'failed';
    if (err instanceof ApiError) {
      task.error = err.message;
      task.cors = false;
      if (err.code === 'upload_closed') {
        // S3 dropped the upload; a retry starts it again from the first byte.
        task.fileId = null;
        task.etags.clear();
        task.partLoaded.clear();
        task.urls.clear();
        task.loaded = 0;
      }
    } else {
      await explainFailure(task, err);
    }
    publish();
  }
}

function newTask(qc: QueryClient, entityType: RecordKind, entityId: string, file: File): Task {
  return {
    key: crypto.randomUUID(),
    fileId: null,
    entityType,
    entityId,
    name: file.name,
    size: file.size,
    mime: file.type || null,
    status: 'starting',
    loaded: 0,
    error: null,
    cors: false,
    file,
    partSize: 0,
    partCount: 0,
    etags: new Map(),
    partLoaded: new Map(),
    md5s: new Map(),
    urls: new Map(),
    verify: false,
    xhrs: new Set(),
    stopped: false,
    qc,
  };
}

/** Starts uploading files to a person or company. */
export function startUploads(qc: QueryClient, entityType: RecordKind, entityId: string, files: FileList | File[]): void {
  for (const file of [...files]) {
    if (file.size === 0) {
      toast(`${file.name} is empty, so it was left out`, { error: true });
      continue;
    }
    if (file.size > MAX_FILE_BYTES) {
      toast(`${file.name} is over 5 TB, the most S3 can hold in one file`, { error: true });
      continue;
    }
    const task = newTask(qc, entityType, entityId, file);
    tasks.set(task.key, task);
    void run(task);
  }
  publish();
}

/**
 * Picks up an upload that stopped when its page closed, from the same file chosen again. The parts S3 already holds
 * are compared with the file, and only the missing or different ones are sent.
 */
export function resumeUpload(qc: QueryClient, entityType: RecordKind, entityId: string, item: FileItem, file: File): void {
  if (file.name !== item.name || file.size !== item.size) {
    toast(`To resume this upload, choose ${item.name} (${formatBytes(item.size)})`, { error: true });
    return;
  }
  const task = newTask(qc, entityType, entityId, file);
  task.fileId = item.id;
  task.mime = item.mime;
  task.status = 'failed';
  task.verify = true;
  tasks.set(task.key, task);
  void retryUpload(task.key);
}

/** Picks a failed upload up again, skipping the parts S3 already holds. */
export async function retryUpload(key: string): Promise<void> {
  const task = tasks.get(key);
  if (!task || task.status !== 'failed') return;
  task.status = 'starting';
  task.error = null;
  task.cors = false;
  task.stopped = false;
  publish();
  if (task.fileId) {
    try {
      const state = await api.post<{ partSize: number; partCount: number; parts: { partNumber: number; etag: string; size: number }[] | null }>(
        `/files/${task.fileId}/resume`,
      );
      if (task.stopped) return;
      planFor(task, state.partSize, state.partCount);
      task.partLoaded.clear();
      task.urls.clear();
      if (state.parts) {
        task.etags.clear();
        const held = state.parts.filter((p) => p.size === partBytes(task, p.partNumber));
        if (task.verify && held.length) {
          task.status = 'checking';
          refreshLoaded(task);
          publish();
        }
        for (const p of held) {
          if (task.verify && !(await sameBytes(task, p.partNumber, p.etag))) continue;
          task.etags.set(p.partNumber, p.etag);
          refreshLoaded(task);
        }
      }
      task.verify = false;
      refreshLoaded(task);
    } catch (err) {
      if (task.stopped) return;
      if (err instanceof ApiError && err.code === 'upload_closed') {
        task.fileId = null;
        task.verify = false;
        task.etags.clear();
        task.partLoaded.clear();
        task.loaded = 0;
      } else {
        task.status = 'failed';
        if (err instanceof PartError) await explainFailure(task, err);
        else task.error = errorMessage(err);
        publish();
        return;
      }
    }
  }
  void run(task);
}

/** Stops an upload and tells S3 to drop the parts sent so far. */
export function cancelUpload(key: string): void {
  const task = tasks.get(key);
  if (!task) return;
  task.stopped = true;
  for (const x of task.xhrs) x.abort();
  tasks.delete(key);
  publish();
  if (task.fileId) {
    void api
      .post(`/files/${task.fileId}/abort`)
      .catch(() => undefined)
      .finally(() => void task.qc.invalidateQueries({ queryKey: keys.files(task.entityType, task.entityId) }));
  }
}
